import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { useAuth } from "@/context/AuthContext";
import { RichText, RichTextView, plainToHtml } from "@/components/cms/RichText";
import { MediaPicker } from "@/components/cms/MediaPicker";
import { BackgroundEditor } from "@/components/cms/BackgroundEditor";
import {
  Loader2, Save, ArrowLeft, ExternalLink, Plus, Trash2, History, MapPin,
} from "lucide-react";

const NAVY = "#1C4358";
const SERVICE_OPTS = ["", "sell_home", "home_safety", "move_into_my_home", "senior_placement", "downsizing", "home_repairs", "partner_referral"];
const APPROVAL = ["draft", "approved", "archived"];
const ic = "text-[15px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0";

const StringListEditor = ({ label, items = [], onChange, readOnly, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <Label className="text-[13px] font-semibold text-[#1C4358]">{label}</Label>
    {items.map((v, i) => (
      <div key={i} className="flex gap-2">
        <Input className={ic} value={v} disabled={readOnly} onChange={(e) => { const n = [...items]; n[i] = e.target.value; onChange(n); }} />
        {!readOnly && <Button type="button" variant="ghost" size="icon" onClick={() => onChange(items.filter((_, x) => x !== i))} className="text-[#9C1E22] shrink-0"><Trash2 size={15} /></Button>}
      </div>
    ))}
    {!readOnly && <Button type="button" variant="outline" size="sm" onClick={() => onChange([...items, ""])} className="gap-1 border-[#D6E0E7] text-[#1C4358]"><Plus size={14} /> Add</Button>}
  </div>
);

const ObjectListEditor = ({ label, items = [], fields, onChange, readOnly, testid }) => (
  <div className="space-y-3" data-testid={testid}>
    <Label className="text-[13px] font-semibold text-[#1C4358]">{label}</Label>
    {items.map((it, i) => (
      <Card key={i} className="p-3 border-[#E6EDF2] space-y-2">
        {fields.map((f) => (
          f.textarea ? (
            <Textarea key={f.key} rows={2} className={ic} placeholder={f.label} disabled={readOnly}
              value={it[f.key] || ""} onChange={(e) => { const n = [...items]; n[i] = { ...n[i], [f.key]: e.target.value }; onChange(n); }} />
          ) : (
            <Input key={f.key} className={ic} placeholder={f.label} disabled={readOnly}
              value={it[f.key] || ""} onChange={(e) => { const n = [...items]; n[i] = { ...n[i], [f.key]: e.target.value }; onChange(n); }} />
          )
        ))}
        {!readOnly && <Button type="button" variant="ghost" size="sm" onClick={() => onChange(items.filter((_, x) => x !== i))} className="text-[#9C1E22] gap-1"><Trash2 size={14} /> Remove</Button>}
      </Card>
    ))}
    {!readOnly && <Button type="button" variant="outline" size="sm" onClick={() => onChange([...items, {}])} className="gap-1 border-[#D6E0E7] text-[#1C4358]"><Plus size={14} /> Add</Button>}
  </div>
);

export default function ServiceLaneEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const readOnly = user?.role !== "super_admin";
  const [lane, setLane] = useState(null);
  const [audit, setAudit] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [keywords, setKeywords] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/site/service-lanes/${id}`);
      setLane(data.lane);
      setAudit(data.audit || []);
      setOverrides(data.local_overrides || []);
      setKeywords((data.lane.seo_keywords || []).join(", "));
    } catch { toast.error("Could not load service lane."); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setLane((l) => ({ ...l, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: lane.name, slug: lane.slug, service_interest: lane.service_interest || null,
        hero_image_url: lane.hero_image_url, hero_image_alt: lane.hero_image_alt, hero_background: lane.hero_background || null,
        hero_headline: lane.hero_headline, hero_subheadline: lane.hero_subheadline,
        intro_copy: lane.intro_copy, intro_copy_html: lane.intro_copy_html,
        who_for: lane.who_for, common_situations: lane.common_situations,
        we_help_with: lane.we_help_with, questions_first: lane.questions_first, partners_involved: lane.partners_involved,
        opportunity_copy: lane.opportunity_copy, opportunity_copy_html: lane.opportunity_copy_html,
        cta_primary_label: lane.cta_primary_label, cta_primary_href: lane.cta_primary_href,
        cta_call_label: lane.cta_call_label, cta_chat_label: lane.cta_chat_label, faqs: lane.faqs, related_resources: lane.related_resources,
        seo_title: lane.seo_title, seo_description: lane.seo_description,
        seo_keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean),
        active: lane.active, approval_status: lane.approval_status,
      };
      const { data } = await api.put(`/site/service-lanes/${id}`, payload);
      setLane(data.lane);
      toast.success("Service lane saved. Public page updated.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;
  if (!lane) return null;

  return (
    <div className="max-w-3xl mx-auto pb-24" data-testid="service-lane-editor">
      <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-white/95 backdrop-blur border-b border-[#E6EDF2] flex items-center justify-between gap-3">
        <button onClick={() => navigate("/site/service-lanes")} className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#1C4358] hover:opacity-70"><ArrowLeft size={16} /> Lanes</button>
        <div className="flex items-center gap-2">
          <a href={`/services/${lane.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#1C4358] px-3 h-10 rounded-lg hover:bg-[#1C4358]/6">Preview <ExternalLink size={15} /></a>
          {!readOnly && (
            <Button onClick={save} disabled={saving} data-testid="lane-save-btn" className="gap-1.5 bg-[#9C1E22] hover:bg-[#861A1D] text-white">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4">
        <h1 className="text-xl font-display font-bold text-[#1C4358]">{lane.name}</h1>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[rgba(28,67,88,0.10)] text-[#1C4358]">GLOBAL</span>
      </div>
      {readOnly && <p className="text-[13px] text-[#9C1E22] mt-1">Read-only — only the Super Admin can edit global content.</p>}
      <p className="text-[12px] text-[#4B5563] mt-1">Last edited by {lane.updated_by_email || "—"} · {lane.updated_at ? new Date(lane.updated_at).toLocaleString() : "—"}</p>

      <Accordion type="multiple" defaultValue={["basics", "hero"]} className="mt-4 space-y-3">
        <AccordionItem value="basics" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="font-display font-semibold text-[#1C4358]">Basics, status & SEO</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">Name</Label><Input data-testid="edit-name" className={ic} value={lane.name || ""} disabled={readOnly} onChange={(e) => set("name", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">Slug</Label><Input data-testid="edit-slug" className={ic} value={lane.slug || ""} disabled={readOnly} onChange={(e) => set("slug", e.target.value)} /></div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold text-[#1C4358]">Maps to lead service</Label>
                <Select value={lane.service_interest || "none"} disabled={readOnly} onValueChange={(v) => set("service_interest", v === "none" ? null : v)}>
                  <SelectTrigger className={ic} data-testid="edit-service-interest"><SelectValue /></SelectTrigger>
                  <SelectContent>{SERVICE_OPTS.map((o) => <SelectItem key={o || "none"} value={o || "none"}>{o || "None"}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold text-[#1C4358]">Approval</Label>
                <Select value={lane.approval_status} disabled={readOnly} onValueChange={(v) => set("approval_status", v)}>
                  <SelectTrigger className={ic} data-testid="edit-approval"><SelectValue /></SelectTrigger>
                  <SelectContent>{APPROVAL.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={!!lane.active} disabled={readOnly} onCheckedChange={(v) => set("active", v)} data-testid="edit-active" />
                <span className="text-[14px] text-[#252221]">{lane.active ? "Active (public)" : "Inactive"}</span>
              </div>
            </div>
            {readOnly ? (
              lane.hero_image_url ? <img src={lane.hero_image_url} alt={lane.hero_image_alt || ""} className="h-32 rounded-lg border border-[#E6EDF2] object-cover" /> : null
            ) : (
              <MediaPicker label="Hero image" testid="lane-hero-media"
                value={lane.hero_image_url || ""} alt={lane.hero_image_alt || ""}
                onUrlChange={(url) => set("hero_image_url", url)} onAltChange={(alt) => set("hero_image_alt", alt)} />
            )}
            <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">SEO title</Label><Input data-testid="edit-seo-title" className={ic} value={lane.seo_title || ""} disabled={readOnly} onChange={(e) => set("seo_title", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">SEO description</Label><Textarea rows={2} className={ic} value={lane.seo_description || ""} disabled={readOnly} onChange={(e) => set("seo_description", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">SEO keywords (comma separated)</Label><Input className={ic} value={keywords} disabled={readOnly} onChange={(e) => setKeywords(e.target.value)} /></div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="hero" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="font-display font-semibold text-[#1C4358]">Hero & intro</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">Headline</Label><Textarea data-testid="edit-headline" rows={2} className={ic} value={lane.hero_headline || ""} disabled={readOnly} onChange={(e) => set("hero_headline", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">Subheadline</Label><Textarea rows={2} className={ic} value={lane.hero_subheadline || ""} disabled={readOnly} onChange={(e) => set("hero_subheadline", e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label className="text-[13px] font-semibold text-[#1C4358]">Intro copy</Label>
              {readOnly
                ? <RichTextView html={lane.intro_copy_html || plainToHtml(lane.intro_copy)} className="rounded-lg border border-[#E6EDF2] bg-[#F7FAFC] p-3 text-[14px]" />
                : <RichText testid="edit-intro-copy" value={lane.intro_copy_html || plainToHtml(lane.intro_copy)} onChange={(html) => set("intro_copy_html", html)} />}
            </div>
            {!readOnly && <BackgroundEditor testid="lane-bg-editor" value={lane.hero_background} onChange={(bg) => set("hero_background", bg)} />}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="content" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="font-display font-semibold text-[#1C4358]">Page content</AccordionTrigger>
          <AccordionContent className="space-y-5 pb-5">
            <StringListEditor label="Who this is for" items={lane.who_for} readOnly={readOnly} onChange={(v) => set("who_for", v)} testid="edit-who-for" />
            <StringListEditor label="Common situations" items={lane.common_situations} readOnly={readOnly} onChange={(v) => set("common_situations", v)} />
            <StringListEditor label="What MovingMyMama helps with" items={lane.we_help_with} readOnly={readOnly} onChange={(v) => set("we_help_with", v)} />
            <StringListEditor label="What questions we ask first" items={lane.questions_first} readOnly={readOnly} onChange={(v) => set("questions_first", v)} />
            <StringListEditor label="Partners that may be involved" items={lane.partners_involved} readOnly={readOnly} onChange={(v) => set("partners_involved", v)} />
            <div className="space-y-1.5">
              <Label className="text-[13px] font-semibold text-[#1C4358]">Public opportunity/revenue-safe wording</Label>
              {readOnly
                ? <RichTextView html={lane.opportunity_copy_html || plainToHtml(lane.opportunity_copy)} className="rounded-lg border border-[#E6EDF2] bg-[#F7FAFC] p-3 text-[14px]" />
                : <RichText testid="edit-opportunity-copy" value={lane.opportunity_copy_html || plainToHtml(lane.opportunity_copy)} onChange={(html) => set("opportunity_copy_html", html)} />}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="cta" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="font-display font-semibold text-[#1C4358]">CTAs</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">Primary CTA label</Label><Input className={ic} value={lane.cta_primary_label || ""} disabled={readOnly} onChange={(e) => set("cta_primary_label", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">Primary CTA href</Label><Input className={ic} value={lane.cta_primary_href || ""} disabled={readOnly} onChange={(e) => set("cta_primary_href", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">Call CTA label</Label><Input className={ic} value={lane.cta_call_label || ""} disabled={readOnly} onChange={(e) => set("cta_call_label", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">Chat CTA label</Label><Input className={ic} value={lane.cta_chat_label || ""} disabled={readOnly} onChange={(e) => set("cta_chat_label", e.target.value)} /></div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="faqs" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="font-display font-semibold text-[#1C4358]">FAQs & resources</AccordionTrigger>
          <AccordionContent className="space-y-5 pb-5">
            <ObjectListEditor label="FAQs" items={lane.faqs} readOnly={readOnly} onChange={(v) => set("faqs", v)} testid="edit-faqs"
              fields={[{ key: "q", label: "Question" }, { key: "a", label: "Answer", textarea: true }]} />
            <ObjectListEditor label="Related resources" items={lane.related_resources} readOnly={readOnly} onChange={(v) => set("related_resources", v)}
              fields={[{ key: "title", label: "Title" }, { key: "excerpt", label: "Excerpt", textarea: true }, { key: "href", label: "Link (href)" }]} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="meta" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="font-display font-semibold text-[#1C4358]">Local overrides & audit trail</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <div>
              <div className="text-[12px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2 flex items-center gap-1"><MapPin size={13} /> Franchises with local overrides</div>
              {overrides.length === 0 ? <p className="text-sm text-muted-foreground">None yet.</p> : (
                <ul className="space-y-1.5">{overrides.map((o, i) => (
                  <li key={i} className="text-sm flex items-center justify-between"><span className="text-[#252221]">{o.franchise}</span>
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-[rgba(47,125,90,0.12)] text-[#2F7D5A]">{o.approval_status}{o.active === false ? " · off" : ""}</span></li>
                ))}</ul>
              )}
            </div>
            <div>
              <div className="text-[12px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2 flex items-center gap-1"><History size={13} /> Recent changes</div>
              {audit.length === 0 ? <p className="text-sm text-muted-foreground">No history.</p> : (
                <ul className="space-y-1.5">{audit.slice(0, 10).map((a, i) => (
                  <li key={i} className="text-[13px] text-[#4B5563]">{a.action.replace(/_/g, " ")} · {a.user_email || "system"} · {new Date(a.created_at).toLocaleString()}</li>
                ))}</ul>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
